// "One use per distinct export" for tools whose person can keep editing and re-exporting inside one visit
// (Magazine Maker, Catalog Maker). A tool session normally costs one trial use in total (usageGate.ts). That
// let someone export, change the content, and export again for nothing. Here every export carries a key made
// from what went into it; a key already taken in this visit may be downloaded again free, and a new key
// (changed content) costs another use.

const taken = new Map<string, Set<string>>();

/** FNV-1a 32-bit over a string, as 8 hex digits. */
export function hashString(s: string, seed = 0x811c9dc5): string {
  let h = seed >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** Same for bytes (e.g. a picture). */
export function hashBytes(b: Uint8Array): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < b.length; i++) {
    h ^= b[i];
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${b.length}-${h.toString(16)}`;
}

/** A stable key for the inputs of an export. Pass plain data (strings, numbers, booleans, arrays, objects); Uint8Array is hashed. */
export function exportKey(inputs: unknown): string {
  const json = JSON.stringify(inputs, (_k, v) => (v instanceof Uint8Array ? `bytes:${hashBytes(v)}` : v));
  return `${hashString(json)}${hashString(json, 0x9747b28c)}`;
}

export type ExportPlan = "free" | "first" | "again";

/**
 * free  = this exact export was already taken in this visit (download it again at no cost).
 * first = nothing taken yet in this visit: the normal one-use-per-visit gate applies.
 * again = something else was taken before: this changed export costs a new use.
 */
export function planExport(toolId: string, key: string): ExportPlan {
  const seen = taken.get(toolId);
  if (seen?.has(key)) return "free";
  return seen && seen.size > 0 ? "again" : "first";
}

export function commitExport(toolId: string, key: string): void {
  let s = taken.get(toolId);
  if (!s) taken.set(toolId, (s = new Set()));
  s.add(key);
}

/** Forget a tool's exports (the visit ended). */
export function endExports(toolId: string): void {
  taken.delete(toolId);
}

export function __resetExportsForTests(): void {
  taken.clear();
}
